import { Variables } from '@catapultsports/referee-react';
import { AePeriod } from 'activity-editor/context';
import { Axis, PointerEventObject, XAxisPlotBandsEventsOptions } from 'highcharts';
import { HighchartsReactRefObject } from 'highcharts-react-official';
import { PeriodAthlete, Rotation } from 'oas';

/** Minimum height for a stacked graph in pixel*/
export const ACTIVITY_EDITOR_MIN_CHART_HEIGHT = 135;
export const MIN_ZOOM_RANGE_MS = 10000;
export const MIN_ROTATION_DURATION_MS = 1000;
export const ROTATION_SPLIT_BUFFER = 0.05; // 5% buffer for rotation split
export const DRAG_PROXIMITY = 0.02; // Percentage of chart range where ew-resize cursor is shown for dragging rotation bounds

export const getActivityGraphColor = (index: number): string => {
    const COLORS = ['#007C8C', '#1D4058', '#5F8EAF', '#5A2B8C', '#FFC300', '#2A468B'];
    index = index > 0 ? index : COLORS.length;
    return COLORS[(COLORS.length - 1) % index];
};

export function ensurePositiveLength<T extends Partial<CustomXAxisPlotBandOptions>>(
    newPlotBand: T,
    activeSide: MouseState['activeSide'],
): T {
    if (newPlotBand.to !== undefined && newPlotBand.from !== undefined) {
        if (activeSide === 'to') {
            newPlotBand.to = Math.max(newPlotBand.to, newPlotBand.from);
        } else if (activeSide === 'from') {
            newPlotBand.from = Math.min(newPlotBand.from, newPlotBand.to);
        }
    }

    return newPlotBand;
}

export function setZoneAndOpacityTrace(rotationPlotBands: CustomXAxisPlotBandOptions[], period: AePeriod) {
    let boundaries: { time: number; opacity: number }[] = [];

    //set opacity of the first zone if it is an off rotation
    if (rotationPlotBands[0]?.from !== undefined && rotationPlotBands[0].from > period.startDateTime.getTime()) {
        boundaries.push({ time: period.startDateTime.getTime(), opacity: 1 });
    }

    boundaries = boundaries.concat(
        rotationPlotBands.flatMap((rotation) => [
            { time: rotation.from ?? 0, opacity: 0.5 },
            { time: rotation.to ?? 0, opacity: 1 },
        ]),
    );

    //set opacity of the last zone if it is an off rotation
    if ((rotationPlotBands[rotationPlotBands.length - 1]?.to ?? Infinity) < period.endDateTime.getTime()) {
        boundaries.push({ time: period.endDateTime.getTime(), opacity: 0.5 });
    }

    return boundaries;
}

export interface MouseState {
    canDrag: boolean;
    dragStartXValue?: number; // Current x-value of mouse pointer
    xAxis?: Axis; // Reference ot xAxis of graph that is hovered
    isDragging: boolean;
    startLimit?: number; // Minimum value we can drag to
    endLimit?: number; // Maximum value we can drag to
    activeSide?: 'from' | 'to'; // Which side of the plot band is being dragged
    activeMetadata?: CustomXAxisPlotBandOptions['metadata']; // Metadata for the plot band that is being dragged
    hasCreatedOffRotation?: boolean;
    isWithinBuffer?: boolean;
}

export interface CustomXAxisPlotBandOptions extends Highcharts.XAxisPlotBandsOptions {
    metadata: {
        plotBandType: 'period' | 'rotation' | 'offRotation';
        period: AePeriod;
        periodAthlete: PeriodAthlete;
    };
}

export type UpdateRotationsFunc = (periodId: string, athleteId: string, newRotations: Rotation[]) => void;

export const getRotationId = (
    periodAthleteId: string,
    startTimeMs: number | string,
    endTimeMs: number | string,
): string => `${periodAthleteId}:${startTimeMs}:${endTimeMs}`;

export const getRotationPlotBand = (
    id: string,
    startTimeMs: number,
    endTimeMs: number,
    period: AePeriod,
    periodAthlete: PeriodAthlete,
    plotBandType: 'rotation' | 'offRotation' | 'period',
    events?: XAxisPlotBandsEventsOptions,
): CustomXAxisPlotBandOptions => ({
    id,
    from: startTimeMs,
    to: endTimeMs,
    zIndex: 2,
    metadata: {
        plotBandType,
        period,
        periodAthlete,
    },
    events,
});

export const getPlotbandLabelStyle = (
    renderAsIndividual: boolean,
    isInPeriod: boolean,
    isSelected: boolean,
    index: number,
): Partial<Highcharts.XAxisLabelsOptions> => {
    let color: string | undefined;
    let backgroundColor: string | undefined;
    let border: string | undefined;
    let y = 2;
    if (renderAsIndividual) {
        if (isInPeriod) {
            border = '1px solid var(--r-catapult-black)';
            if (isSelected) {
                color = Variables.css?.catapultWhite;
                backgroundColor = Variables.css?.catapultBlack;
            } else {
                color = Variables.css?.catapultBlack;
                backgroundColor = Variables.css?.catapultWhite;
            }
        } else {
            color = Variables.css?.grey3;
            backgroundColor = Variables.css?.catapultWhite;
        }
    } else {
        y = 10;
    }

    return {
        useHTML: true,
        style: {
            zIndex: isInPeriod ? 99999 : index,
            color,
            backgroundColor,
            border,
            fontWeight: 'bold',
            fontFamily: 'Mark Pro',
            fontSize: '12px',
            padding: '0 12px',
            display: 'flex',
            alignItems: 'center',
            // @ts-expect-error - Issue with usage of number, using string with px instead
            height: '20px',
            // @ts-expect-error - Issue with usage of number, using string with px instead
            borderRadius: '16px',
            transform: 'translate(-12px, 0)', // compensate for left padding
        },
        y,
    };
};

export const getActivityGraphAthleteIdentifier = (
    deviceId: number,
    graphParamType: string,
    graphType: string,
): string => `${deviceId}:${graphParamType}:${graphType}`;

export const getAthleteDisplayName = (
    jerseyNumber: string | undefined,
    firstName: string | undefined,
    lastName: string | undefined,
): string => {
    return `${jerseyNumber} ${firstName?.slice(0, 1)}.${lastName}`;
};

export const getXAxis = (chart: Highcharts.Chart, event: PointerEventObject): Axis | undefined => {
    const pointerxAxisIndex = getPointerXaxisIndex(chart, event);
    return pointerxAxisIndex !== undefined ? chart.xAxis[pointerxAxisIndex] : undefined;
};

export const getPlotBands = (chart: Highcharts.Chart, event: PointerEventObject): CustomXAxisPlotBandOptions[] => {
    const xAxis = getXAxis(chart, event);
    return (xAxis?.userOptions?.plotBands || []) as CustomXAxisPlotBandOptions[];
};

export const getXValue = (chart: Highcharts.Chart, event: PointerEventObject): number => {
    const xAxis = getXAxis(chart, event);
    if (xAxis === undefined) {
        return 0;
    }

    const chartX = chart.pointer.normalize(event).chartX;
    return xAxis.toValue(chartX);
};

// Get the index for the current plot that is hovered
const getPointerXaxisIndex = (chart: HighchartsReactRefObject['chart'], event: PointerEventObject) => {
    const coords = chart.pointer.getCoordinates(event);

    return coords.yAxis.reduce((acc, cur, index) => {
        if (acc === undefined && cur.value > 0) {
            return index;
        }
        return acc;
    }, undefined as number | undefined);
};

export const getChartTemplate = (): Highcharts.Options => ({
    chart: {
        events: {
            load: function () {
                const seriesGroups = this.container.querySelectorAll('.highcharts-series-group');
                seriesGroups.forEach((group) => {
                    group.setAttribute('data-z-index', '4');
                });
            },
            //disable default selection functionality
            selection: function (event) {
                event.preventDefault;
                return false;
            },
        },
        reflow: true,
        showAxes: true,
        zoomType: 'x',
        marginLeft: 0,
        marginRight: 0,
        marginBottom: 60,
        marginTop: 0,
    },

    time: {
        useUTC: false,
    },
    title: {
        text: undefined,
    },
    legend: {
        enabled: false,
    },
    xAxis: [
        {
            crosshair: true,
            type: 'datetime',
            plotBands: [],
            plotLines: [],
            height: '100%',
            labels: {
                enabled: true,
                style: {
                    color: Variables.css?.catapultBlack,
                    fontSize: '12px',
                },
            },
            gridLineWidth: 1,
            top: undefined,
            offset: undefined,
            linkedTo: undefined,
        },
    ],
    yAxis: [
        {
            crosshair: false,
            title: {
                text: undefined,
            },
            height: '100%',
            labels: {
                enabled: true,
                align: 'left',
                x: 5,
                y: -3,
                style: {
                    color: 'var(--r-catapult-black)',
                    fontSize: '12px',
                },
            },
            showLastLabel: false,
            top: undefined,
            offset: undefined,
            linkedTo: undefined,
        },
    ],
    series: [],
    rangeSelector: {
        enabled: true,
        selected: 0,
    },
    plotOptions: {
        line: {
            lineWidth: 0.5,
        },
        area: {
            fillOpacity: 0.4,
        },
        series: {
            states: {
                inactive: {
                    opacity: 1,
                },
            },
            showInNavigator: true,
            animation: false,
        },
    },

    credits: {
        enabled: false,
    },
    tooltip: {
        // Position the tooltip at the top of each series and to the right of the point, or if it's too wide, to the left
        positioner: function (tooltipWidth, _tooltipHeight, point) {
            const horizontalProximity = 15; // horizontal distance between point and tooltip

            // Distance of chart container from edges of document
            const chartContainerLeft = this.chart.container.getBoundingClientRect().left;
            const chartContainerTop = this.chart.container.getBoundingClientRect().top;

            const defaultX = chartContainerLeft + point.plotX + this.chart.plotLeft + horizontalProximity;

            const x =
                defaultX + tooltipWidth > this.chart.plotWidth
                    ? defaultX - tooltipWidth - 2 * horizontalProximity
                    : defaultX;

            const nSeries = this.chart.series.length;
            const seriesHeight = Math.ceil(this.chart.plotHeight / nSeries);
            const seriesNumber = Math.ceil(point.plotY / seriesHeight);

            // Position tooltip at top of each series
            const seriesMargin = 13;
            const y = chartContainerTop + (seriesNumber - 1) * seriesHeight + seriesMargin;

            return { x, y };
        },
        xDateFormat: '%a, %b %e, %H:%M:%S',
        shape: 'square',
        outside: true,
    },
});

export const yAxisTitle: Highcharts.YAxisTitleOptions = {
    align: 'high',
    rotation: 0,
    margin: 0,
    y: -12,
    x: 50,
    style: {
        color: 'var(--r-catapult-black)',
        fontWeight: 'bold',
        fontFamily: 'Mark Pro',
        whiteSpace: 'nowrap',
    },
    textAlign: 'center',
};

export const yAxisLabel = {
    align: 'left',
    x: 5,
    y: -3,
    style: {
        color: 'var(--r-catapult-black)',
        fontSize: '10px',
    },
} as Highcharts.YAxisLabelsOptions;

export const xAxisLabel = {
    align: 'center',
    x: 0,
    y: 20,
    style: {
        color: Variables.css?.catapultBlack,
        fontSize: '12px',
    },
} as Highcharts.XAxisLabelsOptions;

export const isRotationsEqual = (rotation1: Rotation[], rotation2: Rotation[]): boolean =>
    rotation1.length === rotation2.length &&
    rotation1.every((r1) =>
        rotation2.some((r2) => r1.start_time_ms === r2.start_time_ms && r1.end_time_ms === r2.end_time_ms),
    );

export interface plotBand {
    id?: string;
    metadata: {
        plotBandType: 'rotation' | 'period' | 'offRotation';
        period: AePeriod;
        periodAthlete: PeriodAthlete;
    };
    acrossPanes?: boolean;
    borderColor?: string;
    className: string | undefined;
    events?: Highcharts.XAxisPlotBandsEventsOptions;
    from?: number;
    to?: number;
    zIndex?: number;
}
export const mapGanttToPlotBandActiveSide = (pointStart: number, pointEnd: number) => {
    return {
        start: { from: new Date(pointStart) },
        end: { to: new Date(pointEnd) },
        center: { from: new Date(pointStart), to: new Date(pointEnd) },
    };
};

export const updateChartsExtremes = (charts: Highcharts.Chart[], zoom: [number, number]) => {
    charts.forEach((chart) => {
        chart?.xAxis[0].setExtremes(zoom[0], zoom[1], true, false);
    });
};
export const updatePlotband = (
    chart: Highcharts.Chart,
    updatedDateTime: { from?: Date; to?: Date },
    periodId: string,
) => {
    const userOptions = chart.xAxis[0].userOptions;

    if (chart) {
        const newPlotBands: Highcharts.XAxisPlotBandsOptions[] | undefined = userOptions?.plotBands?.map(
            (plotBand: Partial<CustomXAxisPlotBandOptions>) => {
                if (plotBand?.metadata?.plotBandType === 'period' && plotBand?.metadata?.period?.id === periodId) {
                    return {
                        ...plotBand,
                        from: updatedDateTime.from ? updatedDateTime.from.getTime() : plotBand.from,
                        to: updatedDateTime.to ? updatedDateTime.to.getTime() : plotBand.to,
                    };
                }
                return plotBand;
            },
        );

        chart.xAxis[0].update({ plotBands: newPlotBands }, true);
    }
};
